import { Component, effect, forwardRef, inject, signal } from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { BuildingBaseData, Coordinates } from '@artemis-software/wr-api';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { createForm } from '@sonofabit/ng-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NbDialogService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { formatCoordinatesTooltip } from '@pages/building-detail-page/coordinate-utils';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import {
  CoordinateDialogComponent,
  zeroCoordinates
} from '@components/dialog/coordinate-dialog/coordinate-dialog.component';

@Component({
  standalone: false,
  selector: 'wr-building-base-data-form',
  templateUrl: './building-base-data-form.component.html',
  styleUrl: './building-base-data-form.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingBaseDataFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BuildingBaseDataFormComponent),
      multi: true,
    }
  ],
})
export class BuildingBaseDataFormComponent extends AbstractFormControl<BuildingBaseData> implements Validator {
  private readonly dialogService = inject(NbDialogService);

  readonly showResetCoordinates = signal(false);

  readonly form = createForm(({ field }) => {
    return {
      name: field({ value: '', validators: [Validators.required] }),
      sectionText: field({ value: '' as string | null }),
      externalNumber: field({ value: '' as string | null }),
      buildingType: field({ value: null as BuildingBaseData.BuildingTypeEnum | null, validators: [Validators.required] }),
      otherBuildingType: field({ value: '' as string | null }),
      buildingClass: field({ value: null as BuildingBaseData.BuildingClassEnum | null, validators: [Validators.required] }),
      otherBuildingClass: field({ value: '' as string | null }),
      area: field({ value: null as number | null }),
      floorCountWithoutCellar: field({ value: '' as string | null }),
      coordinates: field({ value: null as Coordinates | null }),
      uid: field({ value: '' as string | null }),
      insuranceNumber: field({ value: '' as string | null }),
      insuranceCompany: field({ value: undefined as BuildingBaseData.InsuranceCompanyEnum | undefined }),
    };
  });

  constructor() {
    super();
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(val => {
      this.value = val as BuildingBaseData;
      this.onValidatorChange?.();
    });

    effect(() => {
      const coordinates = this.form.get('coordinates')?.value;
      this.showResetCoordinates.set(!!coordinates);
    });
  }

  override setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  hasError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  override writeValue(value: (BuildingBaseData) | undefined) {
    super.writeValue(value);
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
    } else {
      this.form.reset({}, { emitEvent: false });
    }
  }

  override markAsTouched() {
    super.markAsTouched();
    this.form.markAllAsTouched();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.form.invalid ? { baseDataInvalid: true } : null;
  }

  coordinateTooltip(): string {
    const coords = this.form.get('coordinates')?.value;
    if (!coords || coords.latitude === undefined) return 'Keine Koordinaten vorhanden';
    return formatCoordinatesTooltip(coords as { latitude: number; longitude: number });
  }

  async resetCoordinates() {
    const result = await firstValueFrom(this.dialogService.open(DeleteDialogComponent, {
      context: { title: 'Koordinaten zurücksetzen', message: 'Bist du sicher?' }
    }).onClose);
    if (result) this.form.get('coordinates')?.setValue(null);
  }

  async openCoordinateDialog() {
    const dialogRef = this.dialogService.open(CoordinateDialogComponent, {
      context: {
        title: 'Koordinaten',
        coordinates: this.form.get('coordinates')?.value ?? zeroCoordinates()
      },
    });
    const result = await firstValueFrom(dialogRef.onClose);
    if (result) this.form.get('coordinates')?.setValue(result);
  }

  private onValidatorChange?: () => void;
  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }
}
